import apiService from '../services/apiService';
import Milestone from '../models/Milestone';

export const milestoneController = {
  // Get all milestones with optional filters
  getAll: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const endpoint = queryParams ? `milestones?${queryParams}` : 'milestones';
      const response = await apiService.get(endpoint);
      const items = response.data?.map(item => Milestone.fromJSON(item)) || [];
      return { success: true, data: items, total: response.total || items.length };
    } catch (error) {
      return { success: false, data: [], message: error.message || 'Failed to fetch milestones' };
    }
  },

  // Get single milestone by ID
  getById: async (id) => {
    try {
      const response = await apiService.get(`milestones/${id}`);
      return { success: true, data: Milestone.fromJSON(response.data) };
    } catch (error) {
      return { success: false, data: null, message: error.message };
    }
  },

  // Get milestones by project
  getByProject: async (projectId) => {
    try {
      const response = await apiService.get(`milestones?projectId=${projectId}`);
      const items = response.data?.map(item => Milestone.fromJSON(item)) || [];
      return { success: true, data: items };
    } catch (error) {
      return { success: false, data: [], message: error.message };
    }
  },

  // Create new milestone
  create: async (data) => {
    try {
      // Transform deliverables array to match backend schema
      const transformedData = { ...data };
      if (transformedData.deliverables && Array.isArray(transformedData.deliverables)) {
        transformedData.deliverables = transformedData.deliverables.map(d => {
          if (typeof d === 'string') {
            return {
              name: d,
              description: '',
              isCompleted: false
            };
          }
          return d;
        });
      }
      
      const item = new Milestone(transformedData);
      const validation = item.validate();
      if (!validation.isValid) {
        return { success: false, errors: validation.errors };
      }
      const response = await apiService.post('milestones', item.toJSON());
      return { 
        success: true, 
        data: Milestone.fromJSON(response.data), 
        message: 'Milestone created successfully' 
      };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Update existing milestone
  update: async (id, data) => {
    try {
      // Transform deliverables array to match backend schema
      const transformedData = { ...data };
      if (transformedData.deliverables && Array.isArray(transformedData.deliverables)) {
        transformedData.deliverables = transformedData.deliverables.map(d => {
          if (typeof d === 'string') {
            return {
              name: d,
              description: '',
              isCompleted: false
            };
          }
          return d;
        });
      }
      
      const item = new Milestone({ ...transformedData, id });
      const validation = item.validate();
      if (!validation.isValid) {
        return { success: false, errors: validation.errors };
      }
      const response = await apiService.put(`milestones/${id}`, item.toJSON());
      return { 
        success: true, 
        data: Milestone.fromJSON(response.data), 
        message: 'Milestone updated successfully' 
      };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Update milestone status
  updateStatus: async (id, status) => {
    try {
      const updateData = { status };
      if (status === 'Completed') {
        updateData.completedDate = new Date().toISOString();
        updateData.progress = 100;
      }
      const response = await apiService.patch(`milestones/${id}/status`, updateData);
      return { 
        success: true, 
        data: Milestone.fromJSON(response.data), 
        message: 'Status updated' 
      };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Update milestone progress
  updateProgress: async (id, progress) => {
    try {
      const response = await apiService.patch(`milestones/${id}/progress`, { progress });
      return { 
        success: true, 
        data: Milestone.fromJSON(response.data), 
        message: 'Progress updated' 
      };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Delete milestone
  delete: async (id) => {
    try {
      await apiService.delete(`milestones/${id}`);
      return { success: true, message: 'Milestone deleted successfully' };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Bulk update milestones
  bulkUpdate: async (milestoneIds, updates) => {
    try {
      const response = await apiService.post('milestones/bulk-update', { milestoneIds, updates });
      return { success: true, data: response.data, message: 'Milestones updated successfully' };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Bulk delete milestones
  bulkDelete: async (milestoneIds) => {
    try {
      await apiService.post('milestones/bulk-delete', { milestoneIds });
      return { success: true, message: 'Milestones deleted successfully' };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Export milestones
  export: async (filters = {}, format = 'csv') => {
    try {
      const queryParams = new URLSearchParams({ ...filters, format }).toString();
      const response = await apiService.get(`milestones/export?${queryParams}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Get milestone statistics
  getStatistics: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const endpoint = queryParams ? `milestones/statistics?${queryParams}` : 'milestones/statistics';
      const response = await apiService.get(endpoint);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, data: null, message: error.message };
    }
  },

  // Add deliverable to milestone
  addDeliverable: async (id, deliverable) => {
    try {
      const response = await apiService.post(`milestones/${id}/deliverables`, { deliverable });
      return { success: true, data: response.data, message: 'Deliverable added' };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Remove deliverable from milestone
  removeDeliverable: async (id, deliverableId) => {
    try {
      const response = await apiService.delete(`milestones/${id}/deliverables/${deliverableId}`);
      return { success: true, data: response.data, message: 'Deliverable removed' };
    } catch (error) {
      return { success: false, message: error.message };
    }
  }
};

export default milestoneController;